'use client';

import type { SeoStatisticsBlockResponseDto } from '@gen/types/SeoStatisticsBlockResponseDto';
import { startTransition, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ProductsListClient } from '../components/shared/ProductsList/SearchResults/ProductsListClient';
import {
  telegramGeoFindAnyById,
  TelegramProductsControllerFindAllQueryParams,
  telegramProductsFindAll,
  TelegramProductShortResponseDto,
  seoMetaGetMeta,
} from '@/generated';
import { SearchFiltersList } from '../components/shared/SearchFiltersList';

import { useFiltersState } from '../components/shared/SearchFiltersList/useFilersState';
import { appendCategoryParam, createCorrectGeoPathFromData } from '@/utils/SEOPathModule/utils';
import { Dicts } from '@/utils/SEOPathModule/types';
import { useCityId, useCountryId, useRegionId, useSetLocationWithoutCheck, useWLMode } from '@/contexts/CookieContext';
import { useFiltersLocalState } from '../components/shared/SearchFiltersList/useFiltersLocalState';
import { BaseSearch } from '../components/shared/SearchFiltersList/BaseSearch';
// import { ActiveFilters } from '../components/shared/SearchFiltersList/ActiveFilters';
// import type { ParamItem } from '@/utils/SEOPathModule/convertPathToParams';
import { PER_PAGE } from './consts';
import FoundAmount from './FoundAmount';
import { GuestSeoTextBlock } from '../components/shared/SeoTextBlock/GuestSeoTextBlock';
import { CategoryFallbackSelected } from '../components/shared/SearchFiltersList/FiltersCategorySelect/FallbackCategorySelect';
import { GeoAwareBreadcrumbs } from '../components/shared/Breadcrumbs/GeoAwareBreadcrumbs.client';
import { sendGTMEvent } from '@next/third-parties/google';
import { DictsGateState } from '../components/shared/SearchFiltersList/types';
import { useMediaQuerySafe } from '@/hooks/useMediaQuerySafe';
import { useIsClient } from 'usehooks-ts';
import { useTranslations } from 'next-intl';
import { appendFreeBreadcrumb } from '@/utils/SEOPathModule/convertPathToParams';

type Props = {
  initialProducts: TelegramProductShortResponseDto[];
  dicts: Dicts;
  initialTotal: number;
  statisticsBlocks?: SeoStatisticsBlockResponseDto[];
  statisticsUpdatedAt?: string | null;
  metaHeader?: string;
  metaSeoBlockHeader: string;
  metaSeoBlockText: string;
  metaTitle: string;
  metaDescription: string;
  categoryInitialData: CategoryFallbackSelected;
  initialSearchLink: string;
  isLoggedIn: boolean;
  isMobileDevice?: boolean;
};

type PendingAction =
  | { type: 'submit' }
  | { type: 'submitWithCategory'; newCategories: string[] }
  | { type: 'clickMore' }
  | { type: 'changeSort'; newSortBy: string }
  | { type: 'geoSync'; newPath: string };

export const ClientPage: React.FC<Props> = ({
  initialProducts,
  statisticsBlocks,
  statisticsUpdatedAt,
  metaHeader,
  initialTotal,
  dicts: { categoryIdDict, categoryNameDict, geoDict },
  metaSeoBlockHeader,
  metaSeoBlockText,
  metaTitle,
  metaDescription,
  categoryInitialData,
  initialSearchLink,
  isLoggedIn,
  isMobileDevice = false,
}) => {
  const {
    geo,
    params,
    commonParams,
    categories,
    updateFiltersState,
    updateGeo,
    breadcrumbs,
    cancelAllSelectedByNewSearch,
    isDictsReady,
    isDictsLoading,
    isDictsError,
    hasAnyParamInPath,
    pathParams,
  } = useFiltersState({ categoryIdDict, categoryNameDict, geoDict });
  const lastActiveSearchString = commonParams.search;
  const tProduct = useTranslations('product');
  const isFreeInPath = commonParams.isFree === true;
  const shownBreadcrumbs = useMemo(
    () => (isFreeInPath ? appendFreeBreadcrumb([...breadcrumbs], tProduct('free_product')) : breadcrumbs),
    [breadcrumbs, isFreeInPath, tProduct],
  );

  const countryId = useCountryId();
  const regionId = useRegionId();
  const cityId = useCityId();
  const updateFullLocation = useSetLocationWithoutCheck();
  const wlMode = useWLMode();
  const initialGeoEffectFired = useRef<boolean>(false);

  const isDesktop = useMediaQuerySafe('(min-width: 1024px)');
  const isClient = useIsClient();

  const initialLocalValue = useMemo(() => {
    return {
      params,
      commonParams,
      categories: categories.map((el) => el.id),
    };
  }, [params, commonParams, categories]);

  const {
    shownParameters,
    priceValue,
    onChangePrice,
    onChangeIsFree,
    onUpdateParam,
    onUpdateCategory,
    onChangeSearch,
    onCancel,
    searchValue,
    isFree,
    showFree,
    filtersLocalValue,
    onDropParamsSearchChanged,
  } = useFiltersLocalState({
    initialValue: initialLocalValue,
    initialDicts: {
      categoryIdDict: categoryIdDict,
    },
  });
  const [products, setProducts] = useState(initialProducts);
  const [total, setTotal] = useState(initialTotal);
  const [metaState, setMetaState] = useState({
    statisticsBlocks,
    statisticsUpdatedAt,
    metaHeader,
    metaSeoBlockHeader,
    metaSeoBlockText,
    metaTitle,
    metaDescription,
  });

  useEffect(() => {
    sendGTMEvent({
      event: 'search_page_view',
      has_text_query: commonParams.search?.trim() !== undefined && commonParams.search?.trim() != '',
      amount_of_ads_found: initialTotal,
    });
    if (initialTotal === 0) {
      sendGTMEvent({
        event: 'search_page_view_nothing_found',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isLoading, setIsLoading] = useState(false);
  const isLoadingMoreRef = useRef(false);
  const [dictsGateState, setDictsGateState] = useState<DictsGateState>('idle');
  const pendingActionRef = useRef<PendingAction | null>(null);

  const loadNewProducts = useCallback(
    async (filters: any, reloadingMode: boolean = false) => {
      const { geo, commonParams, parameters: categoryParams, categories } = filters;

      setIsLoading(true);
      if (reloadingMode) {
        setProducts([]);
      }
      let dropParamsMode = false;
      let convertedParams: TelegramProductsControllerFindAllQueryParams = {};

      if ('countryId' in geo && geo.countryId) {
        convertedParams.countryId = geo.countryId;
      }

      if ('regionId' in geo && geo.regionId) {
        convertedParams.regionId = geo.regionId;
      }

      if ('cityId' in geo && geo.cityId) {
        convertedParams.cityId = geo.cityId;
      }
      if (categories && categories.length > 0) {
        convertedParams.categoryId = categories[categories.length - 1];
      }
      convertedParams.param = {};
      for (const elem of categoryParams) {
        appendCategoryParam(convertedParams, elem.id, elem.value);
      }

      const shouldHidePrice = commonParams.isFree === true;

      convertedParams = {
        ...convertedParams,
        skip: commonParams.skip || 0,
        take: PER_PAGE,

        searchOnlyInTitle: commonParams.searchOnlyInTitle === 'true',
        isFree: commonParams.isFree,
        sortBy: commonParams.sortBy || '',
        search: commonParams.search || '',
        newSearchQuery: commonParams.search && commonParams.search != lastActiveSearchString,
        ...(!shouldHidePrice
          ? {
              minPrice: commonParams.minPrice ? parseInt(commonParams.minPrice) : 0,
              maxPrice: commonParams.maxPrice ? parseInt(commonParams.maxPrice) : undefined,
            }
          : {}),
      };

      try {
        const { data: newProducts, total, searchFiltersDropped } = await telegramProductsFindAll(convertedParams);

        if (reloadingMode && searchFiltersDropped) {
          cancelAllSelectedByNewSearch(commonParams.search);
          onDropParamsSearchChanged();
          dropParamsMode = true;
        }

        if (reloadingMode) {
          setProducts(newProducts);
        } else {
          setProducts((prev) => [...prev, ...newProducts]);
        }

        setTotal(total);

        if (reloadingMode) {
          sendGTMEvent({
            event: 'search_page_view',
            has_text_query: commonParams.search?.trim() !== undefined && commonParams.search?.trim() != '',
            amount_of_ads_found: total,
          });
          if (total === 0) {
            sendGTMEvent({
              event: 'search_page_view_nothing_found',
            });
          }

          try {
            let metaParams: any = {
              ...geo,
              searchQuery: convertedParams.search || '',
              categoryId: categories && categories.length > 0 ? categories[categories.length - 1] : undefined,
            };

            for (const elem of categoryParams) {
              appendCategoryParam(metaParams, elem.id, elem.value);
            }

            if (convertedParams.minPrice !== undefined) {
              metaParams.minPrice = convertedParams.minPrice;
            }

            if (convertedParams.maxPrice !== undefined) {
              metaParams.maxPrice = convertedParams.maxPrice;
            }

            if (convertedParams.searchOnlyInTitle !== undefined) {
              metaParams.searchOnlyInTitle = convertedParams.searchOnlyInTitle;
            }

            if (convertedParams.isFree !== undefined) {
              metaParams.isFree = convertedParams.isFree;
            }

            if (dropParamsMode) {
              metaParams = {
                ...geo,
                searchQuery: convertedParams.search || '',
              };
            }

            const meta = await seoMetaGetMeta('search', metaParams);
            setMetaState({
              statisticsBlocks: meta.statisticsBlocks,
              statisticsUpdatedAt: meta.statisticsUpdatedAt,
              metaHeader: meta.h1 || '',
              metaSeoBlockText: meta.bottomSeoText || '',
              metaSeoBlockHeader: meta.bottomSeoTitle || '',
              metaTitle: meta.title || '',
              metaDescription: meta.description || '',
            });
          } catch (error) {
            setMetaState((previous) => ({ ...previous, statisticsBlocks: [], statisticsUpdatedAt: null }));
            console.error('Failed to fetch updated meta header', error);
          }
        }
      } finally {
        setIsLoading(false);
      }
    },
    [lastActiveSearchString, cancelAllSelectedByNewSearch, onDropParamsSearchChanged, setMetaState],
  );

  const executePendingAction = useCallback(
    (action: PendingAction) => {
      if (action.type === 'submit') {
        if (typeof window !== 'undefined') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }

        const filters = updateFiltersState(
          { ...filtersLocalValue.commonParams, skip: 0 },
          filtersLocalValue.params,
          filtersLocalValue.categories,
        );
        loadNewProducts(filters, true);
        return;
      }

      if (action.type === 'submitWithCategory') {
        if (typeof window !== 'undefined') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }

        onUpdateCategory(action.newCategories);
        const filters = updateFiltersState(
          { ...filtersLocalValue.commonParams, isFree: undefined, skip: 0 },
          filtersLocalValue.params,
          action.newCategories,
        );
        loadNewProducts(filters, true);
        return;
      }

      if (action.type === 'clickMore') {
        if (isLoadingMoreRef.current) return;
        isLoadingMoreRef.current = true;
        const filters = updateFiltersState(
          { ...commonParams },
          params,
          categories.map((val) => val.id),
        );
        const newSkip = Math.floor(products.length / PER_PAGE) * PER_PAGE;
        loadNewProducts({ ...filters, commonParams: { ...filters.commonParams, skip: newSkip } }).finally(() => {
          isLoadingMoreRef.current = false;
        });
        return;
      }

      if (action.type === 'changeSort') {
        const filters = updateFiltersState(
          { ...commonParams, sortBy: action.newSortBy, skip: 0 },
          params,
          categories.map((val) => val.id),
        );
        loadNewProducts(filters, true);
        return;
      }

      const filters = updateGeo(action.newPath);
      loadNewProducts(
        {
          ...filters,
          geo: {
            cityId,
            regionId,
            countryId,
          },
        },
        true,
      );
    },
    [
      updateFiltersState,
      filtersLocalValue,
      loadNewProducts,
      onUpdateCategory,
      commonParams,
      params,
      categories,
      products.length,
      updateGeo,
      cityId,
      regionId,
      countryId,
    ],
  );

  const executePendingActionRef = useRef(executePendingAction);

  useLayoutEffect(() => {
    executePendingActionRef.current = executePendingAction;
  }, [executePendingAction]);

  const runPathAction = useCallback(
    (action: PendingAction) => {
      if (isDictsReady) {
        startTransition(() => {
          executePendingActionRef.current(action);
        });
        return;
      }

      if (isDictsError) {
        pendingActionRef.current = null;
        setDictsGateState('error');
        return;
      }

      if (!pendingActionRef.current) {
        pendingActionRef.current = action;
        setDictsGateState('loading');
      }
    },
    [isDictsReady, isDictsError],
  );

  useEffect(() => {
    if (dictsGateState === 'idle') {
      return;
    }

    if (isDictsError) {
      pendingActionRef.current = null;
      setDictsGateState('error');
    }
  }, [isDictsError, dictsGateState]);

  useEffect(() => {
    if (!isDictsReady || !pendingActionRef.current) {
      return;
    }

    const pendingAction = pendingActionRef.current;
    pendingActionRef.current = null;
    setDictsGateState('idle');
    executePendingAction(pendingAction);
  }, [isDictsReady, executePendingAction]);

  useEffect(() => {
    if (dictsGateState === 'idle') {
      return;
    }

    if (isDictsLoading) {
      setDictsGateState('loading');
    }
  }, [isDictsLoading, dictsGateState]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    if (metaState.metaTitle) {
      document.title = metaState.metaTitle;
    }

    if (metaState.metaDescription) {
      let descriptionTag = document.querySelector('meta[name="description"]');
      if (!descriptionTag) {
        descriptionTag = document.createElement('meta');
        descriptionTag.setAttribute('name', 'description');
        document.head.appendChild(descriptionTag);
      }

      descriptionTag.setAttribute('content', metaState.metaDescription);
    }
  }, [metaState.metaTitle, metaState.metaDescription]);

  // this useeffect' logic fires only on opening this page
  // it should work only once, setting the geo of request as a main geo
  useEffect(() => {
    if (initialGeoEffectFired.current) {
      return;
    }

    if (!countryId && !regionId && !cityId) {
      return;
    }

    if ('cityId' in geo) {
      if (cityId !== geo.cityId) {
        telegramGeoFindAnyById(geo.cityId).then((res) => {
          const coords = {
            lat: res.city?.latitude || 0,
            lon: res.city?.longitude || 0,
          };
          initialGeoEffectFired.current = true;
          updateFullLocation(res.country?.id || '', res.region?.id || '', geo.cityId, coords);
        });
        return;
      }
    }

    if ('regionId' in geo) {
      if (regionId !== geo.regionId || cityId) {
        telegramGeoFindAnyById(geo.regionId).then((res) => {
          const coords = {
            lat: res.region?.latitude || 0,
            lon: res.region?.longitude || 0,
          };
          initialGeoEffectFired.current = true;
          updateFullLocation(res.country?.id || '', geo.regionId, '', coords);
        });
        return;
      }
    }

    if ('countryId' in geo) {
      if (countryId !== geo.countryId || regionId || cityId) {
        telegramGeoFindAnyById(geo.countryId).then((res) => {
          const coords = {
            lat: res.country?.latitude || 0,
            lon: res.country?.longitude || 0,
          };
          initialGeoEffectFired.current = true;
          updateFullLocation(geo.countryId, '', '', coords);
        });
        return;
      }
    }
    initialGeoEffectFired.current = true;
  }, [countryId, regionId, cityId, geo, updateFullLocation]);

  const lastProcessedGeo = useRef<string>('');

  useEffect(() => {
    if (!countryId && !regionId && !cityId) {
      return;
    }

    if (lastProcessedGeo.current === `${countryId}-${regionId}-${cityId}`) {
      return;
    }
    lastProcessedGeo.current = `${countryId}-${regionId}-${cityId}`;

    if (!initialGeoEffectFired.current) {
      return;
    }

    if ('cityId' in geo) {
      if (cityId === geo.cityId) {
        return;
      }
    }

    if ('regionId' in geo) {
      if (regionId === geo.regionId && !cityId) {
        return;
      }
    }

    if ('countryId' in geo) {
      if (countryId === geo.countryId && !cityId && !regionId) {
        return;
      }
    }

    createCorrectGeoPathFromData({
      cityId,
      regionId,
      countryId,
      existingGeo: geo,
      isWLMode: wlMode,
      updateGeo: (newPath: string) => {
        runPathAction({ type: 'geoSync', newPath });
      },
    });
  }, [countryId, regionId, cityId, geo, wlMode, runPathAction]);

  const onSubmit = useCallback(() => {
    runPathAction({ type: 'submit' });
  }, [runPathAction]);

  const onSubmitWithCategory = useCallback(
    (newCategories: string[]) => {
      runPathAction({ type: 'submitWithCategory', newCategories });
    },
    [runPathAction],
  );

  const onClickMore = useCallback(() => {
    runPathAction({ type: 'clickMore' });
  }, [runPathAction]);

  const onChangeSort = useCallback(
    (newSortBy: string) => {
      runPathAction({ type: 'changeSort', newSortBy });
    },
    [runPathAction],
  );

  /*
  const [isSubmitQueued, setIsSubmitQueued] = useState(false);

  useEffect(() => {
    if (isSubmitQueued) {
      setIsSubmitQueued(false);
      onSubmit();
    }
  }, [isSubmitQueued, onSubmit]);

  const paramNames = useMemo(
    () => Object.fromEntries(shownParameters.map((param) => [param.id, param.name || ''])),
    [shownParameters],
  );

  const onRemoveAppliedParam = useCallback(
    (id: string, nextValue: ParamItem['value']) => {
      onUpdateParam(id, nextValue);
      setIsSubmitQueued(true);
    },
    [onUpdateParam],
  );

  const onRemoveAppliedPrice = useCallback(() => {
    onChangePrice({});
    setIsSubmitQueued(true);
  }, [onChangePrice]);

  const onRemoveAppliedFree = useCallback(() => {
    onChangeIsFree(false);
    setIsSubmitQueued(true);
  }, [onChangeIsFree]);

  const onClearAllApplied = useCallback(() => {
    onCancel();
    setIsSubmitQueued(true);
  }, [onCancel]);
  */

  const hasMore = products.length < total;

  const currentSort = commonParams.sortBy;

  return (
    <>
      <div className='flex gap-[12px] mt-[12px] lg:mt-[48px] relative md:flex-row flex-col'>
        <BaseSearch
          shownParameters={shownParameters}
          searchValue={searchValue || ''}
          params={filtersLocalValue.params}
          price={priceValue}
          isFree={isFree}
          showFree={showFree}
          categories={filtersLocalValue.categories}
          onChangePrice={onChangePrice}
          onChangeIsFree={onChangeIsFree}
          onUpdateCategory={onUpdateCategory}
          onUpdateParam={onUpdateParam}
          onClickSearch={onSubmit}
          onSearchChange={onChangeSearch}
          onChangeSort={onChangeSort}
          sort={currentSort || ''}
          onClear={onCancel}
          dictsGateState={dictsGateState}
        />
      </div>
      <div className='lg:mt-[32px] mt-[16px] block'>
        {shownBreadcrumbs.length > 1 && (
          <GeoAwareBreadcrumbs
            initialSearchLink={initialSearchLink}
            hideLastElem={pathParams.length > 0 || isFreeInPath ? true : !hasAnyParamInPath}
            breadcrumbs={shownBreadcrumbs}
          />
        )}
      </div>

      <FoundAmount metaHeader={metaState.metaHeader} total={total} />

      <div className='flex flex-row w-full md:gap-[30px] gap-[12px]'>
        {isClient && !isDesktop ? (
          <></>
        ) : (
          <div className='hidden lg:block min-w-[300px] w-[300px] self-start'>
            <SearchFiltersList
              categoryInitialData={categoryInitialData}
              onCancel={onCancel}
              onSubmit={onSubmit}
              onSubmitWithCategory={onSubmitWithCategory}
              onChangePrice={onChangePrice}
              isFree={isFree}
              onChangeIsFree={onChangeIsFree}
              onUpdateCategory={onUpdateCategory}
              categories={filtersLocalValue.categories}
              shownParams={shownParameters}
              priceValue={priceValue}
              params={filtersLocalValue.params}
              onUpdateParam={onUpdateParam}
              dictsGateState={dictsGateState}
              showFree={showFree}
            />
          </div>
        )}
        <div className='w-full md:mt-[15px]'>
          {/* <ActiveFilters
            params={params}
            paramNames={paramNames}
            minPrice={commonParams.minPrice}
            maxPrice={commonParams.maxPrice}
            isFree={commonParams.isFree}
            onRemoveParam={onRemoveAppliedParam}
            onRemovePrice={onRemoveAppliedPrice}
            onRemoveFree={onRemoveAppliedFree}
            onClearAll={onClearAllApplied}
          /> */}
          <ProductsListClient
            hasTextSearch={!!(commonParams.search && commonParams.search.trim())}
            onChangeSort={onChangeSort}
            sort={currentSort || ''}
            products={products}
            showMore={hasMore}
            isLoading={isLoading}
            onClickLoadMore={onClickMore}
            isWideInitially={!isMobileDevice}
          />
        </div>
      </div>

      <GuestSeoTextBlock
        statisticsBlocks={isLoading ? undefined : metaState.statisticsBlocks}
        statisticsUpdatedAt={metaState.statisticsUpdatedAt}
        initialIsLoggedIn={isLoggedIn}
        header={metaState.metaSeoBlockHeader}
        seoText={metaState.metaSeoBlockText}
      />
    </>
  );
};
